/**
 * <s-product-gallery> — single-image product gallery on a scroll-snap track.
 *
 * Required child markup:
 *   [data-gallery-track]  — horizontal scroll-snap track; each direct child is one slide.
 *   [data-gallery-prev] / [data-gallery-next] — arrow buttons.
 *   [data-gallery-current] — element whose text is the 1-based current index.
 *   [data-gallery-thumb]  — thumbnail buttons, paired with slides by order.
 */
class SProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;
    this.slides = Array.from(this.track.children);
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.current = this.querySelector('[data-gallery-current]');
    this.index = 0;

    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.go(i)));

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            this.update(this.slides.indexOf(entry.target));
          }
        }
      },
      { root: this.track, threshold: [0.6] }
    );
    this.slides.forEach((slide) => this.observer.observe(slide));

    // Variant → media: when a shopper picks a colour/option, jump to that variant's photo.
    this.variantMedia = {};
    try {
      const json = this.querySelector('[data-gallery-variant-media]')?.textContent;
      if (json) this.variantMedia = JSON.parse(json);
    } catch (error) {
      console.error('[s-product-gallery] Invalid variant media map:', error);
    }
    this.onVariantChange = (event) => {
      if (!event.target.closest?.('s-variant-picker')) return;
      const input = event.target;
      const variantId = input.dataset?.variantId || input.selectedOptions?.[0]?.dataset?.variantId;
      this.showVariant(variantId);
    };
    document.addEventListener('change', this.onVariantChange);

    // Start on the selected variant's photo when one is preselected (e.g. ?variant= in the URL).
    const initialId = new URLSearchParams(location.search).get('variant');
    if (!this.showVariant(initialId, true)) this.update(0);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    document.removeEventListener('change', this.onVariantChange);
  }

  showVariant(variantId, instant = false) {
    if (!variantId) return false;
    const mediaId = this.variantMedia[String(variantId)];
    if (!mediaId) return false;
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index < 0) return false;
    if (instant) {
      this.track.scrollLeft = this.slides[index].offsetLeft - this.slides[0].offsetLeft;
      this.update(index);
    } else {
      this.go(index);
    }
    return true;
  }

  go(target) {
    const total = this.slides.length;
    if (!total) return;
    const next = (target + total) % total;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Offsets relative to the first slide work in both LTR and RTL (RTL scrollLeft is negative).
    const left = this.slides[next].offsetLeft - this.slides[0].offsetLeft;
    this.track.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
    this.update(next);
  }

  update(i) {
    if (i < 0) return;
    this.index = i;
    if (this.current) this.current.textContent = String(i + 1);
    this.thumbs.forEach((thumb, n) => {
      const active = n === i;
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
      if (active) {
        const strip = thumb.closest('[data-gallery-thumbs]');
        if (strip) {
          const target = thumb.offsetLeft - (strip.clientWidth - thumb.clientWidth) / 2;
          strip.scrollTo({ left: target, behavior: 'auto' });
        }
      }
    });
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
